/**
 * CoachContextMenu — Right-click context menu on played cards and tricks.
 * Allows asking the professor why a card was played, rewinding,
 * and commenting.
 */

import { useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';

interface CoachContextMenuProps {
  x: number;
  y: number;
  card: number;
  player: number;
  playerName: string;
  trickNumber: number;
  positionInTrick?: number;
  socket: Socket | null;
  onClose: () => void;
  onRewindCard?: (positionInTrick: number, trickNumber?: number) => void;
  onCommentCard?: (trickNumber: number, positionInTrick: number) => void;
  onCommentTrick?: (trickNumber: number) => void;
  /** If true, show "Commenter ce pli" instead of card-level options */
  trickMode?: boolean;
}

export default function CoachContextMenu({
  x, y, card, player, playerName, trickNumber, positionInTrick,
  socket, onClose, onRewindCard, onCommentCard, onCommentTrick, trickMode,
}: CoachContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleExplain = () => {
    if (socket) {
      socket.emit('training:coach_explain', { card, player, trickNumber });
    }
    onClose();
  };

  const handleRewind = () => {
    if (positionInTrick !== undefined && onRewindCard) {
      onRewindCard(positionInTrick, trickNumber);
    }
    onClose();
  };

  const handleCommentCard = () => {
    if (positionInTrick !== undefined && onCommentCard) {
      onCommentCard(trickNumber, positionInTrick);
    }
    onClose();
  };

  const handleCommentTrick = () => {
    if (onCommentTrick) {
      onCommentTrick(trickNumber);
    }
    onClose();
  };

  const style: React.CSSProperties = {
    left: Math.min(x, window.innerWidth - 260),
    top: Math.min(y, window.innerHeight - 120),
  };

  return (
    <div className="coach-context-menu" ref={ref} style={style}>
      {!trickMode && (
        <button className="coach-context-menu-item" onClick={handleExplain}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
          </svg>
          <span>Pourquoi {playerName} a joué cette carte ?</span>
        </button>
      )}
      {!trickMode && positionInTrick !== undefined && onRewindCard && (
        <button className="coach-context-menu-item coach-context-menu-rewind" onClick={handleRewind}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>
          </svg>
          <span>Modifier cette carte (reprendre ici)</span>
        </button>
      )}
      {!trickMode && positionInTrick !== undefined && onCommentCard && (
        <button className="coach-context-menu-item" onClick={handleCommentCard}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
          </svg>
          <span>Commenter cette carte</span>
        </button>
      )}
      {(trickMode || onCommentTrick) && trickNumber > 0 && (
        <button className="coach-context-menu-item" onClick={handleCommentTrick}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
          </svg>
          <span>Commenter le pli {trickNumber}</span>
        </button>
      )}
    </div>
  );
}
